/**
 * Authentication Application Service
 * 
 * Focused service for authentication use cases
 * Following DDD application layer principles:
 * - Thin orchestration layer
 * - Delegates business logic to domain
 * - Handles cross-cutting concerns
 */
import { PersonAuth, AuthenticationResult } from '../../domain/aggregates/PersonAuth';
import type { IPersonAuthRepository } from '../../domain/repositories/IPersonAuthRepository';
import { AuthSession } from '../../domain/aggregates/AuthSession';
import type { IAuthSessionRepository } from '../../domain/repositories/IAuthSessionRepository';
import { Logger } from '../../../../infrastructure/logging/Logger';
import { 
  InvalidCredentialsError, 
  AccountLockedError,
  DatabaseError 
} from '../../../../infrastructure/errors/production-errors';

export interface LoginCommand {
  email: string;
  password: string;
  rememberMe: boolean;
  ipAddress?: string;
  userAgent?: string;
}

export interface LoginResult {
  success: boolean;
  sessionId?: string;
  personId?: string;
  email?: string;
  expiresAt?: Date;
  error?: string;
  attemptsRemaining?: number;
}

export class AuthenticationService {
  private logger: Logger;

  constructor(
    private personAuthRepo: IPersonAuthRepository,
    private sessionRepo: IAuthSessionRepository,
    private eventBus: { publish: (event: any) => Promise<void> }
  ) {
    this.logger = Logger.getInstance().child({ service: 'AuthenticationService' });
  }

  /**
   * Handle login - thin orchestration, not business logic
   */
  async login(command: LoginCommand): Promise<LoginResult> {
    try {
      // 1. Find aggregate
      const personAuth = await this.personAuthRepo.findByEmail(command.email);
      if (!personAuth) {
        // Don't reveal account doesn't exist
        return {
          success: false,
          error: 'Invalid credentials',
          attemptsRemaining: 5
        };
      }

      // 2. Delegate authentication to domain
      const authResult = await personAuth.authenticate(command.password);

      // 3. Save state changes
      await this.personAuthRepo.save(personAuth);

      // 4. Publish domain events
      await this.publishEvents(personAuth);

      // 5. Create session if successful
      if (authResult.success && authResult.sessionId) {
        const session = await this.createSession(
          personAuth,
          authResult.sessionId!,
          command.rememberMe,
          command.ipAddress,
          command.userAgent
        );

        return {
          success: true,
          sessionId: session.getId(),
          personId: personAuth.getPersonId(),
          email: personAuth.getEmail(),
          expiresAt: session.getExpiresAt()
        };
      }

      // 6. Return failure
      return {
        success: false,
        error: authResult.failureReason || 'Authentication failed',
        attemptsRemaining: authResult.attemptsRemaining
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'An error occurred during login'
      };
    }
  }

  /**
   * Logout - simple and focused
   */
  async logout(sessionId: string): Promise<void> {
    const session = await this.sessionRepo.findById(sessionId);
    if (session) {
      session.invalidate();
      await this.sessionRepo.save(session);
      await this.publishEvents(session);
    }
  }

  private async createSession(
    personAuth: PersonAuth,
    sessionId: string,
    rememberMe: boolean,
    ipAddress?: string,
    userAgent?: string
  ): Promise<AuthSession> {
    const expirationHours = rememberMe ? 24 * 30 : 24; // 30 days or 1 day
    
    const session = AuthSession.create(
      sessionId,
      personAuth.getPersonId(),
      personAuth.getEmail(),
      expirationHours,
      ipAddress,
      userAgent
    );

    await this.sessionRepo.save(session);
    await this.publishEvents(session);
    
    return session;
  }

  private async publishEvents(aggregate: PersonAuth | AuthSession): Promise<void> {
    const events = aggregate.getUncommittedEvents();
    for (const event of events) {
      await this.eventBus.publish(event);
    }
    aggregate.markEventsAsCommitted();
  }
}